import React, { useRef, useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ErrorModal from "./ImportSummaryModal";
import { FileUploadOutlined, DeleteOutline as Delete, InsertDriveFile as FileIcon } from "@mui/icons-material";
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';

import * as XLSX from "xlsx";
import ExcelExportExampleButton from "./ExcelExportExampleButton";
import { bulkCreateTestCaseApiCall } from "../../utils/api";
import { useSnackbar } from "../../utils/useSnackbar";
import CustomButton from '../Button/customButton/CustomButton';
import styles from './ExcelImportExampleInput.module.scss';
import ButtonComponent from "../customButton";
import { MAX_FILE_SIZE } from "../../utils/excelExportHelperFunction";
import { useSelector } from "react-redux";

const validateMetaInfo = (workbook, projectId) => {
  const metaSheet = workbook.Sheets["MetaInfo"];
  if (!metaSheet) return false;
  const metaData = XLSX.utils.sheet_to_json(metaSheet, { header: 1 });
  const generatedBy = metaData[0]?.[1];
  const projectIdInFile = metaData[1]?.[1];
  return generatedBy === "AP-TEST-CASE-IMPORT" && projectIdInFile && projectIdInFile === projectId;
};

const parseDataSheet = (workbook) => {
  const worksheet = workbook.Sheets["Data"];
  if (!worksheet) return [];
  
  // Read module mapping from DropdownOptions sheet
  const dropdownSheet = workbook.Sheets["DropdownOptions"];
  const moduleMapping = {};
  
  if (dropdownSheet) {
    const dropdownData = XLSX.utils.sheet_to_json(dropdownSheet, { header: 1 });
    dropdownData.forEach((row, index) => {
      if (row[0] && row[1]) { // Column A (name) and Column B (value)
        moduleMapping[row[0]] = row[1];
      }
    });
  }
  
  const labelSheet = workbook.Sheets["LabelOptions"];
  const labelMapping = {};
  if (labelSheet) {
    const labelData = XLSX.utils.sheet_to_json(labelSheet, { header: 1 });
    labelData.forEach((row) => {
      if (row[0]) { 
        labelMapping[row[0]] = row[1] || null;
      }
    });
  }

  const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
  // Remove empty rows and header row
  const filteredRows = rows.filter(
    (row) => Object.values(row).some(val => val !== "" && val !== null && val !== undefined)
  );
  
  return filteredRows.map((row) => {
    // Module mapping
    const transformedRow = {
      ...row,
      Module: row.Module && moduleMapping[row.Module]
        ? moduleMapping[row.Module]
        : row.Module,
    };

    // Labels mapping
    if (row.labels) {
      // Support comma-separated labels
      const labelNames = row.labels.split(",").map((l) => l.trim()).filter(Boolean);

      transformedRow.labels = labelNames.map((name) => ({
        name,
        id: labelMapping[name] || null, // if not in sheet mapping, keep id null
      }));
    } else {
      transformedRow.labels = [];
    }

    return transformedRow;
  });
};



const ExcelImportExampleInput = ({ onDataParsed, modulePaths, onImportSuccess , labelOptions }) => {
  const inputRef = useRef();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [importSummary, setImportSummary] = useState({})
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { showSnackbar } = useSnackbar();
  const { selectedProject } = useSelector((state) => state.projects);

  function toogleErrorModal() {
    setIsErrorModalOpen(prev => !prev);
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      setError("File size should be less than 5MB");
      return;
    }
    setSelectedFile(file);
    setError("");
    event.target.value = "";
  };

  const handleBrowseClick = () => {
    inputRef.current.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError("");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (
        file.type === "application/vnd.ms-excel" ||
        file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith('.xls') ||
        file.name.endsWith('.xlsx')
      ) {
        if (file.size > MAX_FILE_SIZE) {
          setError("File size should be less than 5MB");
          return;
        }
        setSelectedFile(file);
        setError("");
      } else {
        setError("Invalid file type. Please upload an .xls or .xlsx file.");
      }
    }
  };

   // Add handler for import
   const handleExcelImport = async (parsedRows) => {
    const response = await bulkCreateTestCaseApiCall({data: parsedRows});
    setImportSummary({
      totalTestCases: response?.totalTestCases,
      correcttestcase: response?.correcttestcase,
      invalidTestCases: response?.invalidTestCases,
      errors: response?.errors || [],
      rowsInvolved: response?.rowsInvolved || [],
    })
    if(response?.code == 'FORBIDDEN'){
       showSnackbar(response?.message || "Error in bulk create test cases", "error");
      return
    }

    if(response?.correcttestcase !== response?.totalTestCases){
      toogleErrorModal();
      const errorMessage = response?.message || "Error importing test cases";
      showSnackbar(errorMessage, "error");
      if (onImportSuccess) onImportSuccess();
      return;
    } else {
      showSnackbar(`${parsedRows.length} test cases imported/updated successfully!`, "success");
      if (onImportSuccess) onImportSuccess();
      return;
    }
  };

  const handleConfirmUpload = () => {
    if (!selectedFile) {
      setError("Please select a file first");
      return;
    }
    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = evt.target.result;
      const workbook = XLSX.read(data, { type: "array" });
      if (!validateMetaInfo(workbook,selectedProject?.id)) {
        setError("Invalid file, please download the import template sheet and try again.");
        return;
      }
      const parsedRows = parseDataSheet(workbook);
      handleExcelImport(parsedRows);
      setModalOpen(false);
      setSelectedFile(null);
      setError("");
    };
    reader.readAsArrayBuffer(selectedFile);
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setModalOpen(false);
    setError("");
  };

  return (
    <>
    <Box>
      <Tooltip title="Import Example Excel">
        <ButtonComponent
          variant="outline"
          ctaText="Import"
          className={'primary'}
          startIcon={<ArrowUpTrayIcon className="icon-small" />}
          onClick={() => setModalOpen(true)}
        />
      </Tooltip>
      <input
        type="file"
        accept=".xlsx,.xls"
        style={{ display: "none" }}
        ref={inputRef}
        onChange={handleFileSelect}
      />
      <Modal
        open={modalOpen}
        onClose={handleCancelUpload}
        aria-labelledby="upload-excel-modal"
        aria-describedby="modal-to-upload-excel"
      >
        <Box className={styles.modalContainer}>
          <Typography id="upload-excel-modal" variant="componentTitle" component="h2" sx={{ mb: 3 }}>
            Import Test Cases
          </Typography>
          <Box className={styles.instructionsBox}>
            <Box className={styles.instructionsHeader}>
              <ArrowUpTrayIcon  className= {`${styles.instructionsIcon} icon-lg`} />
              Upload Instructions
            </Box>
            <ol className={styles.instructionsList}>
              <li>Download the import template sheet using the button below.</li>
              <li>Fill in your data in the downloaded Excel file.</li>
              <li>Save the file in <b>.xlsx</b> or <b>.xls</b> format. Maximum file size: 5 MB.</li>
              <li>Upload the completed file here by clicking "Browse" or dragging and dropping the file.</li>
            </ol>
              <Box className={styles.noteBox}>
                <b>Note:</b> The first 50 rows in the template include dropdown menus for easier data entry.
                If you need to add more test cases, simply copy one of these rows and paste it below to retain the dropdown functionality.
              </Box>
          </Box>
          <Box className={styles.buttonContainer}>
            <ExcelExportExampleButton modulePaths={modulePaths} labelOptions={labelOptions} />
          </Box>
          <Paper
            className={`${styles.uploadArea} ${isDragging ? styles.dragging : ''}`}
            onClick={handleBrowseClick}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Typography variant="body1" className={`${styles.uploadText} ${isDragging ? styles.dragging : ''}`}>
              Drop file or Browse
            </Typography>
            <Typography variant="body2" className={styles.uploadSubtext}>
              Formats: .xlsx, .xls | Maximum file size: 5 MB
            </Typography>
          </Paper>
          {selectedFile && (
            <List className={styles.fileList}>
              <ListItem className={styles.fileItem}>
                <ListItemIcon className={styles.fileIcon}>
                  <FileIcon />
                </ListItemIcon>
                <ListItemText primary={selectedFile.name} />
                <IconButton edge="end" aria-label="delete" onClick={handleRemoveFile}>
                  <Delete />
                </IconButton>
              </ListItem>
            </List>
          )}
          {error && (
            <Typography color="error" className={styles.errorText}>
              {error}
            </Typography>
          )}
          
          <Box className={styles.actionButtons}>
            <ButtonComponent
              variant="primary"
              className="white bg-blue600"
              onClick={handleCancelUpload}
              ctaText={"Cancel"}
            />
            <ButtonComponent
              variant="primary"
              className="white bg-blue600"
              onClick={handleConfirmUpload}
              ctaText={"Confirm"}
              disabled={!selectedFile}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
    <ErrorModal
        open={isErrorModalOpen}
        errors={importSummary?.errors || []}
        onClose={toogleErrorModal}
        totalTestCases={importSummary?.totalTestCases || 0}
        createdTestCases={importSummary?.correcttestcase || 0} 
        invalidTestCases={importSummary?.invalidTestCases}
        rowsInvolved={importSummary?.rowsInvolved || []}
        modulePaths={modulePaths}
        labelOptions={labelOptions}
    />
    </>
  );
};

export default ExcelImportExampleInput; 